import { FC, FormEvent, useContext, useEffect, useState } from "react";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import appStyles from "../../../App.module.css";
import { PortuguesePurchasePathContext } from "../../PortuguesePurchasePath";
import PortugueseQuoteApi from "../../api/PortugueseQuoteApi";
import { IPortuguesePurchasePathContext } from "../../config/interface";
import portugueseConstant from "../../config/portuguese-Constant";
import NextButton from "../buttons/NextButton";
import PrevButton from "../buttons/PrevButton";
import PPFooter from "../common/PPFooter";
import PPLoadingIndicator from "../common/PPLoadingIndicator";
import PPOrderSummary from "../common/PPOrderSummary";
import PPShowApiError from "../common/PPShowApiError";
import PPStepper from "../common/PPStepper";

interface PaymentFormProps {}

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(portugueseConstant.publishableKey!);

const PPPaymentForm: FC<PaymentFormProps> = (props: PaymentFormProps) => {
  const { order } = useContext(PortuguesePurchasePathContext) as IPortuguesePurchasePathContext;

  const [loading, setLoading] = useState<boolean>(false);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response: any = await PortugueseQuoteApi.getPaymentIntent(order.order_number);
      setStripeIntentData(response.data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <PPLoadingIndicator />}

      <div className={`${appStyles.mainContainer} ${appStyles.W600} ${appStyles.p32}`}>
        <div>
          <div className={appStyles.headerWrapper}>
            <div className={appStyles.pageTitle}>
              <h2 className={`${appStyles.colorPrimary}`}>Make Payment</h2>
            </div>
          </div>
          <PPStepper />

          <PPOrderSummary />

          <div className={`${appStyles.formBlock} ${appStyles.bfMarginTop}`}>
            {!loading && stripeIntentData && (
              <Elements stripe={stripePromise} options={{ clientSecret: stripeIntentData.client_secret }}>
                <PaymentFormElement />
              </Elements>
            )}
          </div>
          <PPFooter />
        </div>
      </div>
    </>
  );
};

export default PPPaymentForm;

type FormProps = {};

const PaymentFormElement: FC<FormProps> = (props: FormProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { next, previous } = useContext(PortuguesePurchasePathContext) as IPortuguesePurchasePathContext;

  const stripe = useStripe();
  const elements = useElements();

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsProcessingPayment(true);

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: "",
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
      setIsProcessingPayment(false);
      return;
    } else {
      setIsProcessingPayment(false);
      next();
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit}>
      <div className={`${appStyles.borderBox} ${appStyles.formBox}`}>
        <div className={appStyles.formWrapper}>
          <div className={appStyles.pageSubTitle}>
            <h2 className={appStyles.colorPrimary}>Card information</h2>
          </div>
          <div className={`${appStyles.formRow} ${appStyles.paymentCardWrapper}`}>
            <PaymentElement
              onReady={() => {
                setIsStripeLoading(false);
              }}
            />
          </div>
        </div>
      </div>

      {!isStripeLoading && (
        <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
          <div className={`${appStyles.buttonBlock} ${appStyles.buttonBlockNextPrev}`}>
            <PrevButton onClick={() => previous()} />

            <NextButton
              title={isProcessingPayment ? "Processing.." : "Make Payment"}
              type="submit"
              disable={isProcessingPayment || !stripe || !elements}
            />
          </div>
        </div>
      )}

      {errorMessage && <PPShowApiError msg={errorMessage} />}
    </form>
  );
};
